import React, { useMemo } from 'react';
import { ModelSection } from '../types/ModelSection';
import { TailwindButton } from './Tailwind';
import { cn } from '../lib/utils';
import { Tab } from '@headlessui/react';
import SearchBar from './SearchBar';
import AddButtonMenu from './AddButtonMenu';
import DraggableDataGroup from './Draggables/DraggableDataGroup';
import ModelDataFieldDetailsPanel from './ModelDataFieldDetailsPanel';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { typeMatchesSection, SECTION_INPUT_ROOT_TYPES, SECTION_ALLOWLIST } from '../utils/modelDataMapper';
import { useEditorStore } from '../shared/stores/useEditorStore';
import { useScenarioTree } from '../shared/hooks/queries/useScenarioTree';

export interface ModelSectionDetailProps {
  section: ModelSection;
  onBack: () => void;
  // Props passés depuis FinancialModelEditor
  currentState: any; // État complet avec Input et Output
  currentSensitivity: any;
  reloadState: () => void;
  onDragEnd?: (result: DropResult, variant: 'Input' | 'Output') => void;
  scenario_id: number;
  recreateExcelInputIfSync: () => void;
  showDiffToggled: boolean;
  setShowDiffToggled: (value: boolean) => void;
  searchStrings: { Input: string; Output: string };
  setSearchStrings: (strings: { Input: string; Output: string }) => void;
  userRights: any;
  modelDataSelected: any;
  setModelDataSelected: (item: any) => void;
  sensitivityModifiedIds?: Set<string>;
}

export default function ModelSectionDetail({
  section,
  onBack,
  currentState,
  currentSensitivity,
  reloadState,
  onDragEnd,
  scenario_id,
  recreateExcelInputIfSync,
  showDiffToggled,
  setShowDiffToggled,
  searchStrings,
  setSearchStrings,
  userRights,
  modelDataSelected,
  setModelDataSelected,
  sensitivityModifiedIds,
}: ModelSectionDetailProps) {
  // Subscribe directly to the TanStack Query cache — same pattern as
  // DraggableDataGroup — so that patchTree updates are picked up immediately
  // without waiting for the parent useEffect to propagate currentSection.
  const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
  const { state: treeState } = useScenarioTree(scenarioId);

  // Root items to render as top-level tree roots for this section.
  // For sections 2-5 (Asset/Equity/Corporate Taxation/Bank Accounts) these
  // are the companies; each DraggableDataGroup then prunes sibling branches
  // via the `allowedTypes` prop below.
  const sectionInputItems = useMemo(() => {
    const items = treeState?.['Input']?.tree?.items;
    if (!items) return [];

    const rootId = treeState['Input']?.tree?.rootId;
    // INPUT-side roots only — `SECTION_ROOT_TYPES` is the input∪output union
    // and would surface output-only roots (e.g. `country_fiscality` for the
    // Taxation section) as additional input roots, which is exactly the bug
    // we're avoiding here.
    const rootTypes = SECTION_INPUT_ROOT_TYPES[section.config.id] ?? [];

    return Object.values(items)
      .filter((item: any) => {
        if (item.id === rootId) return false;
        const t = (item.type || item.kind || '').toLowerCase();
        return rootTypes.includes(t);
      })
      .sort((a: any, b: any) => (a.position || 0) - (b.position || 0));
  }, [treeState, section.config.id]);

  const allowedTypes = SECTION_ALLOWLIST[section.config.id];
  const filteredInputItems = sectionInputItems;

  // Clear selection if the selected item no longer exists in the tree
  // (e.g. after deletion via async removeElemFromTree).
  const effectiveSelected = useMemo(() => {
    if (!modelDataSelected) return null;
    const exists = treeState?.['Input']?.tree?.items?.[modelDataSelected.id];
    return exists ? modelDataSelected : null;
  }, [modelDataSelected, treeState]);

  // Fresh tree item for the field panel — reactive to agent cache patches
  // so field components see updated values without deselect/reselect.
  const freshSelectedForPanel = useMemo(() => {
    if (!modelDataSelected) return null;
    return treeState?.['Input']?.tree?.items?.[modelDataSelected.id] ?? null;
  }, [modelDataSelected, treeState]);

  React.useEffect(() => {
    if (modelDataSelected && !effectiveSelected) {
      setModelDataSelected(null);
    }
  }, [effectiveSelected]);

  // Trouver le root ID dynamiquement (l'item sans parent)
  const inputRootId = currentState?.['Input']?.tree?.rootId || '42';

  // Filtrer le root item pour ne garder que les childrensDefinition correspondant à cette section
  const filteredRootItem = useMemo(() => {
    const rootItem = currentState?.['Input']?.tree?.items?.[inputRootId];
    if (!rootItem) return null;
    return {
      ...rootItem,
      childrensDefinition: rootItem.childrensDefinition?.filter(
        (def: any) => typeMatchesSection(def.type, section.config.id)
      ) || [],
    };
  }, [currentState, inputRootId, section.config.id]);

  const rootNodeKey = filteredRootItem ? (filteredRootItem.kind + filteredRootItem.id.toString()) : '';
  const isRootLoading = useEditorStore((s) => s.loadingNodes.has(rootNodeKey));

  const handleDragEnd = (result: DropResult, variant: 'Input' | 'Output') => {
    if (onDragEnd) {
      onDragEnd(result, variant);
    }
  };

  // Helper pour obtenir l'item style (copié de FinancialModelEditor)
  const getItemStyle = (_isDragging: boolean, draggableStyle: any) => ({
    userSelect: "none",
    padding: 2,
    ...draggableStyle
  });

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Breadcrumb + Back Button */}
      <div className="px-8 py-4 border-b border-slate-100 bg-white sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-2">
          <TailwindButton
            variant="ghost"
            size="sm"
            icon="arrow_back"
            onClick={onBack}
          >
            Back to Overview
          </TailwindButton>
          <span className="text-xs text-slate-400 flex items-center gap-1">
            or press
            <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-300 rounded text-[10px] font-mono">Esc</kbd>
          </span>
        </div>

        {/* Section Header */}
        <div className="flex items-center gap-4">
          <div className={cn(
            'size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center'
          )}>
            <span className="material-symbols-outlined text-[28px]">
              {section.config.icon}
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-slate-900">
                {section.config.name}
              </h2>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              {section.config.description}
            </p>
          </div>
        </div>
      </div>

      {/* Content - Input et Field Details */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-8">
          {/* Mobile/Tablet: Tabs */}
          <div className="lg:hidden">
            <Tab.Group>
              <Tab.List className="flex border-b border-slate-200 mb-6">
                <Tab className={({ selected }) => cn(
                  "flex-1 py-3 px-4 text-sm font-medium transition-colors",
                  selected
                    ? "border-b-2 border-primary text-primary bg-primary/5"
                    : "text-slate-500 hover:text-slate-700"
                )}>
                  <span className="flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-[18px]">edit_note</span>
                    Input Items
                  </span>
                </Tab>
                <Tab className={({ selected }) => cn(
                  "flex-1 py-3 px-4 text-sm font-medium transition-colors",
                  selected
                    ? "border-b-2 border-primary text-primary bg-primary/5"
                    : "text-slate-500 hover:text-slate-700"
                )}>
                  <span className="flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-[18px]">info</span>
                    Field Details
                  </span>
                </Tab>
              </Tab.List>
              <Tab.Panels>
                {/* Tab Panel 1: Input Items */}
                <Tab.Panel>
                  <div className="bg-slate-50 rounded-lg border border-slate-100 shadow-sm flex flex-col h-[600px]">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-slate-200 bg-slate-100/50">
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <span className="material-symbols-outlined text-[20px]">edit_note</span>
                        Input
                        <span className="text-sm font-normal text-slate-500">
                          ({filteredInputItems.length} items)
                        </span>
                      </h3>
                    </div>

                    {/* Search + Add */}
                    <div className="px-4 py-3 flex items-center gap-2 border-b border-slate-200">
                      <div className="flex-1">
                        <SearchBar
                          variant="Input"
                          searchStrings={searchStrings}
                          setSearchStrings={setSearchStrings}
                        />
                      </div>
                      {currentSensitivity && currentSensitivity !== 'null' && (
                        <button
                          onClick={() => setShowDiffToggled(!showDiffToggled)}
                          className={cn(
                            "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0",
                            showDiffToggled
                              ? "bg-amber-100 text-amber-800 border border-amber-300"
                              : "bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200"
                          )}
                          title="Show only modified items"
                        >
                          <span className="material-symbols-outlined text-[16px]">filter_list</span>
                          Modified
                        </button>
                      )}
                      {userRights?.right_to_write && currentState?.['Input']?.tree && filteredRootItem && (
                        <AddButtonMenu
                          item={filteredRootItem}
                          tree={currentState['Input'].tree}
                          reloadState={reloadState}
                          bgcolor="#f8fafc"
                          scenario_id={scenario_id}
                          recreateExcelInputIfSync={recreateExcelInputIfSync}
                          level={0}
                          currentState={currentState}
                          allowedTypes={allowedTypes}
                        />
                      )}
                    </div>

                    {/* Items List */}
                    <div className="flex-1 overflow-y-auto p-2">
                      {isRootLoading && (
                        <div className="py-2 pl-8 text-sm text-gray-400 flex items-center gap-2">
                          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                          Loading...
                        </div>
                      )}
                      <DragDropContext onDragEnd={(result) => handleDragEnd(result, 'Input')}>
                        <Droppable droppableId="section-input-droppable-mobile" type="section-input">
                          {(provided, _snapshot) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {sectionInputItems.map((item: any, index: number) => (
                                  <Draggable
                                    key={item.id.toString()}
                                    draggableId={`mobile-${item.id.toString()}`}
                                    index={index}
                                    type="section-input"
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={getItemStyle(
                                          snapshot.isDragging,
                                          provided.draggableProps.style
                                        )}
                                      >
                                        <DraggableDataGroup
                                          item={item}
                                          currentSensitivity={currentSensitivity}
                                          variant="Input"
                                          level={0}
                                          scenario_id={scenario_id}
                                          showDiffToggled={showDiffToggled}
                                          searchStrings={searchStrings}
                                          selected={effectiveSelected}
                                          setSelected={setModelDataSelected}
                                          sensitivityModifiedIds={sensitivityModifiedIds}
                                          allowedTypes={allowedTypes}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </div>
                  </div>
                </Tab.Panel>

                {/* Tab Panel 2: Field Details */}
                <Tab.Panel>
                  <ModelDataFieldDetailsPanel
                    selectedItem={freshSelectedForPanel}
                    variant="Input"
                    currentState={currentState}
                    currentSensitivity={currentSensitivity}
                    recreateExcelInput={recreateExcelInputIfSync}
                    userRights={userRights}
                    scenarioId={scenario_id}
                  />
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>

          {/* Desktop: Side-by-side */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="flex flex-col">
              <div className="bg-slate-50 rounded-lg border border-slate-100 shadow-sm flex flex-col h-[600px]">
                {/* Header */}
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-100/50">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[20px]">edit_note</span>
                    Input
                    <span className="text-sm font-normal text-slate-500">
                      ({filteredInputItems.length} items)
                    </span>
                  </h3>
                </div>

                {/* Search + Add */}
                <div className="px-4 py-3 flex items-center gap-2 border-b border-slate-200">
                  <div className="flex-1">
                    <SearchBar
                      variant="Input"
                      searchStrings={searchStrings}
                      setSearchStrings={setSearchStrings}
                    />
                  </div>
                  {currentSensitivity && currentSensitivity !== 'null' && (
                    <button
                      onClick={() => setShowDiffToggled(!showDiffToggled)}
                      className={cn(
                        "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0",
                        showDiffToggled
                          ? "bg-amber-100 text-amber-800 border border-amber-300"
                          : "bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200"
                      )}
                      title="Show only modified items"
                    >
                      <span className="material-symbols-outlined text-[16px]">filter_list</span>
                      Modified
                    </button>
                  )}
                  {userRights?.right_to_write && currentState?.['Input']?.tree && filteredRootItem && (
                    <AddButtonMenu
                      item={filteredRootItem}
                      tree={currentState['Input'].tree}
                      reloadState={reloadState}
                      bgcolor="#f8fafc"
                      scenario_id={scenario_id}
                      recreateExcelInputIfSync={recreateExcelInputIfSync}
                      level={0}
                      currentState={currentState}
                    />
                  )}
                </div>

                {/* Items List */}
                <div className="flex-1 overflow-y-auto p-2">
                  {isRootLoading && (
                    <div className="py-2 pl-8 text-sm text-gray-400 flex items-center gap-2">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                      Loading...
                    </div>
                  )}
                  <DragDropContext onDragEnd={(result) => handleDragEnd(result, 'Input')}>
                    <Droppable droppableId="section-input-droppable-desktop" type="section-input">
                      {(provided, _snapshot) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {sectionInputItems.map((item: any, index: number) => (
                              <Draggable
                                key={item.id.toString()}
                                draggableId={item.id.toString()}
                                index={index}
                                type="section-input"
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={getItemStyle(
                                      snapshot.isDragging,
                                      provided.draggableProps.style
                                    )}
                                  >
                                    <DraggableDataGroup
                                      item={item}
                                      currentSensitivity={currentSensitivity}
                                      variant="Input"
                                      level={0}
                                      scenario_id={scenario_id}
                                      showDiffToggled={showDiffToggled}
                                      searchStrings={searchStrings}
                                      selected={modelDataSelected}
                                      setSelected={setModelDataSelected}
                                      sensitivityModifiedIds={sensitivityModifiedIds}
                                      allowedTypes={allowedTypes}
                                    />
                                  </div>
                                )}
                              </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>
            </div>

            {/* Field Details Panel */}
            <div className="flex flex-col">
              <ModelDataFieldDetailsPanel
                selectedItem={freshSelectedForPanel}
                variant="Input"
                currentState={currentState}
                currentSensitivity={currentSensitivity}
                recreateExcelInput={recreateExcelInputIfSync}
                userRights={userRights}
                scenarioId={scenario_id}
              />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
